import React, { useRef, useState } from "react";
import Cropper, { ReactCropperElement } from "react-cropper";
import "cropperjs/dist/cropper.css";
import { Modal } from "antd";

interface Props {
    onSave: (croppedDataUrl: string, file?: File) => void;
    onOpenCrop?: () => void;
    onCloseCrop?: () => void;
    aspectRatio?: number;
    maxSizeMB?: number;
    accept?: string;
    children?: React.ReactElement;
}

const ImageUploadCropper: React.FC<Props> = ({
    onSave,
    onOpenCrop,
    onCloseCrop,
    aspectRatio = 16 / 9,
    maxSizeMB = 2,
    accept = "image/*",
    children,
}) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const cropperRef = useRef<ReactCropperElement>(null);
    const [image, setImage] = useState<string>("");
    const [showModal, setShowModal] = useState(false);
    const [originalFile, setOriginalFile] = useState<File | undefined>(undefined);

    const openFileDialog = (ev?: React.MouseEvent) => {
        ev?.preventDefault();
        if (!fileInputRef.current) {
            fileInputRef.current = document.createElement("input");
            fileInputRef.current.type = "file";
            fileInputRef.current.accept = accept;
            fileInputRef.current.onchange = handleFileChange as any;
        }
        fileInputRef.current.click();
    };

    const handleFileChange = (e: any) => {
        const file = e.target.files?.[0];
        if (file) {
            const maxSize = maxSizeMB * 1024 * 1024;
            if (file.size > maxSize) {
                alert(`File size exceeds ${maxSizeMB}MB`);
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                setImage(reader.result as string);
                setOriginalFile(file);
                onOpenCrop && onOpenCrop();
                setShowModal(true);
            };
            reader.readAsDataURL(file);

            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const dataURLToFile = (dataUrl: string, fileName: string): File => {
        const arr = dataUrl.split(",");
        const mimeMatch = arr[0].match(/:(.*?);/);
        const mime = mimeMatch ? mimeMatch[1] : "image/png";
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], fileName, { type: mime });
    };

    const handleSave = () => {
        const cropper = cropperRef.current?.cropper;
        if (!cropper) return;
        const dataUrl = cropper.getCroppedCanvas().toDataURL();
        const fileName = originalFile ? originalFile.name.replace(/\.[^.]+$/, "") + "-cropped.png" : "cropped.png";
        const croppedFile = dataURLToFile(dataUrl, fileName);
        onSave(dataUrl, croppedFile);
        setShowModal(false);
        setImage("");
        setOriginalFile(undefined);
        onCloseCrop && onCloseCrop();
    };

    const handleCancel = () => {
        setShowModal(false);
        setImage("");
        setOriginalFile(undefined);
        onCloseCrop && onCloseCrop();
    };

    const child = children
        ? React.cloneElement(children, {
            onClick: (e: any) => {
                if (children.props.onClick) children.props.onClick(e);
                openFileDialog(e);
            },
        })
        : null;

    return (
        <>
            {child}

            <Modal
                open={showModal}
                title="Crop Image"
                onCancel={handleCancel}
                footer={
                    <>
                        <ul className="m-auto d-flex align-items-center justify-content-end p-0">
                            <li className="pt-0 pb-0 p-2">
                                <button className="btn btn-primary" onClick={handleSave}>
                                    Save
                                </button>
                            </li>
                            <li className="pt-0 pb-0">
                                <button className="btn btn-secondary" onClick={handleCancel}>
                                    Cancel
                                </button>
                            </li>
                        </ul>
                    </>
                }
            >
                <div style={{ width: "100%" }}>
                    <Cropper
                        src={image}
                        style={{ height: 400, width: "100%" }}
                        initialAspectRatio={aspectRatio}
                        guides={true}
                        ref={cropperRef}
                    />
                </div>
            </Modal>
        </>
    );
};

export default ImageUploadCropper;